import { useCallback, useMemo, useState, useSyncExternalStore } from 'react';
import {
  analyzeImport,
  parseSource,
  validateSource,
  type ImportPlan,
  type ImportPreview,
  type ImportReport,
  type SourceValidation,
} from '../lib/referentialImportService';
import {
  cancelImport,
  getImportRunnerState,
  resetImportRunner,
  startImport,
  subscribeImportRunner,
} from '../lib/referentialImportRunner';
import { TRANSLATION_LANGS, type RefItem } from '../lib/referentialService';

interface Props {
  sectors: RefItem[];
  subsectors: RefItem[];
  onClose: () => void;
  onImported: () => void;
}

type StepStatus = 'todo' | 'active' | 'ok' | 'warn' | 'error';

const STATUS_STYLE: Record<StepStatus, { badge: string; ring: string }> = {
  todo: { badge: 'bg-gray-200 text-gray-500', ring: 'border-gray-200' },
  active: { badge: 'bg-[#00244F] text-white animate-pulse', ring: 'border-[#00244F]/40' },
  ok: { badge: 'bg-emerald-500 text-white', ring: 'border-emerald-200' },
  warn: { badge: 'bg-amber-500 text-white', ring: 'border-amber-200' },
  error: { badge: 'bg-red-500 text-white', ring: 'border-red-200' },
};

function Step({
  index,
  title,
  status,
  children,
}: {
  index: number;
  title: string;
  status: StepStatus;
  children?: React.ReactNode;
}) {
  const style = STATUS_STYLE[status];
  return (
    <div className={`border rounded-xl p-4 ${style.ring} ${status === 'todo' ? 'opacity-55' : ''}`}>
      <div className="flex items-center gap-3">
        <span className={`w-6 h-6 shrink-0 rounded-full text-xs font-bold flex items-center justify-center ${style.badge}`}>
          {status === 'ok' ? '✓' : status === 'error' ? '!' : index}
        </span>
        <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
      </div>
      {children && <div className="mt-3 pl-9 text-sm text-gray-600 space-y-2">{children}</div>}
    </div>
  );
}

function Metric({ value, label, tone = 'blue' }: { value: number; label: string; tone?: 'blue' | 'emerald' | 'amber' | 'red' }) {
  const tones = {
    blue: 'text-blue-700 bg-blue-50 border-blue-200',
    emerald: 'text-emerald-700 bg-emerald-50 border-emerald-200',
    amber: 'text-amber-700 bg-amber-50 border-amber-200',
    red: 'text-red-700 bg-red-50 border-red-200',
  } as const;
  return (
    <div className={`rounded-lg border px-3 py-2 text-center ${tones[tone]}`}>
      <div className="text-xl font-bold leading-tight">{value}</div>
      <div className="text-[11px] leading-tight">{label}</div>
    </div>
  );
}

function buildReportText(report: ImportReport): string {
  const lines = [
    `Rapport d'import — ${new Date().toLocaleString('fr-FR')}`,
    `Durée : ${(report.durationMs / 1000).toFixed(1)} s`,
    '',
    `Créés      — postes ${report.created.positions}, groupes ${report.created.skill_groups}, compétences ${report.created.skills}`,
    `Mis à jour — postes ${report.updated.positions}, groupes ${report.updated.skill_groups}, compétences ${report.updated.skills}`,
    `Obsolètes  — postes ${report.retired.positions}, groupes ${report.retired.skill_groups}, compétences ${report.retired.skills}`,
    `Écartés  : ${report.discarded}`,
    `Jamais tentés (arrêt anticipé) : ${report.notAttempted}`,
    `Traductions lancées : ${report.translationsQueued}`,
  ];
  if (report.abortedReason) lines.push('', `ARRÊT : ${report.abortedReason}`);
  if (report.failures.length) {
    lines.push('', `Blocages (${report.failures.length}) :`);
    for (const failure of report.failures) {
      lines.push(`  [${failure.table}] ${failure.name} (${failure.code}) — ${failure.cause}`);
    }
  }
  return lines.join('\n');
}

const ImportWizard = ({ sectors, subsectors, onClose, onImported }: Props) => {
  const runner = useSyncExternalStore(subscribeImportRunner, getImportRunnerState);

  const [sectorId, setSectorId] = useState('');
  const [subsectorId, setSubsectorId] = useState('');
  const [reactivateObsolete, setReactivateObsolete] = useState(false);
  const [obsoleteAbsent, setObsoleteAbsent] = useState(false);
  const [translate, setTranslate] = useState(true);

  const [validation, setValidation] = useState<SourceValidation | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [parsing, setParsing] = useState(false);

  const [plan, setPlan] = useState<ImportPlan | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzeError, setAnalyzeError] = useState<string | null>(null);

  const busy = runner.status === 'running' || runner.status === 'translating';
  const finished = runner.status === 'done' || runner.status === 'cancelled' || runner.status === 'failed';

  const resetDownstream = () => {
    setPlan(null);
    setAnalyzeError(null);
    setObsoleteAbsent(false);
    resetImportRunner();
  };

  const handleFile = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setPreview(null);
    setParseError(null);
    setPlan(null);
    setAnalyzeError(null);
    setObsoleteAbsent(false);
    resetImportRunner();

    const check = validateSource(file);
    setValidation(check);
    if (!check.ok || !check.kind) return;

    setParsing(true);
    try {
      setPreview(await parseSource(file, check.kind));
    } catch (err) {
      setParseError(err instanceof Error ? err.message : String(err));
    } finally {
      setParsing(false);
    }
  }, []);

  const handleAnalyze = async () => {
    const sector = sectors.find((candidate) => candidate.id === sectorId);
    if (!preview || !sector) return;
    setAnalyzing(true);
    setAnalyzeError(null);
    setObsoleteAbsent(false);
    resetImportRunner();
    try {
      setPlan(
        await analyzeImport({
          preview,
          sectorId: sector.id,
          sectorCode: sector.code,
          subsectorId: subsectorId || null,
          reactivateObsolete,
        }),
      );
    } catch (err) {
      setAnalyzeError(err instanceof Error ? err.message : String(err));
    } finally {
      setAnalyzing(false);
    }
  };

  const handleStart = async () => {
    if (!plan) return;
    // La décision prise à l'étape 4 : le fichier décrit-il tout le secteur ?
    await startImport({ ...plan, obsoleteAbsentPositions: obsoleteAbsent }, translate);
    onImported();
  };

  const downloadReport = () => {
    if (!runner.report) return;
    const blob = new Blob([buildReportText(runner.report)], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `rapport-import-${new Date().toISOString().slice(0, 10)}.txt`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const blocking = useMemo(() => plan?.issues.filter((i) => i.level === 'blocking') ?? [], [plan]);

  // Contenu qu'un poste repris par le fichier ne mentionne plus : retiré dans
  // tous les cas. Les postes absents du fichier dépendent de la case ci-dessous.
  const replacedCount = plan ? plan.replaced.groups.length + plan.replaced.skills.length : 0;
  const absentCount = plan ? plan.absent.positions.length : 0;
  const absentChildrenCount = plan ? plan.absent.groups.length + plan.absent.skills.length : 0;

  // On n'importe pas dans une branche rendue obsolète : elle n'est plus proposée.
  const openSectors = useMemo(() => sectors.filter((sector) => sector.active), [sectors]);
  const openSubsectors = useMemo(
    () => subsectors.filter((sub) => sub.active && sub.sectorId === sectorId),
    [subsectors, sectorId],
  );

  const step1: StepStatus = !validation ? 'todo' : validation.ok ? (validation.warnings.length ? 'warn' : 'ok') : 'error';
  const step2: StepStatus = parsing ? 'active' : parseError ? 'error' : preview ? (preview.totalSkills ? 'ok' : 'warn') : 'todo';
  const step3: StepStatus = analyzing ? 'active' : analyzeError ? 'error' : plan ? (blocking.length ? 'warn' : 'ok') : 'todo';
  const step4: StepStatus = busy ? 'active' : runner.report ? 'ok' : plan ? 'warn' : 'todo';
  const step5: StepStatus = runner.status === 'running' ? 'active'
    : runner.status === 'failed' ? 'error'
    : runner.report ? (runner.report.failures.length ? 'warn' : 'ok') : 'todo';
  const step6: StepStatus = finished ? (runner.report?.failures.length ? 'warn' : 'ok') : 'todo';

  const progressPct = runner.progress && runner.progress.total
    ? Math.round((runner.progress.processed / runner.progress.total) * 100)
    : 0;

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50" onClick={busy ? undefined : onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl mx-4 max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Importer un référentiel</h3>
            <p className="text-xs text-gray-500">Six étapes, aucune écriture avant votre validation à l'étape 4.</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-400 hover:text-gray-600"
            title={busy ? 'L’import continue en arrière-plan' : 'Fermer'}
          >
            ✕
          </button>
        </div>

        <div className="p-6 space-y-3">
          <Step index={1} title="Validation du format" status={step1}>
            <div className="border-2 border-dashed border-gray-300 rounded-xl p-5 text-center hover:border-[#00244F]/40 transition-colors">
              <input type="file" accept=".docx,.xlsx,.xls" onChange={handleFile} className="hidden" id="import-file" disabled={busy} />
              <label htmlFor="import-file" className={busy ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}>
                <div className="text-3xl mb-1">📄</div>
                <p className="text-sm font-medium text-gray-700">Choisir un fichier</p>
                <p className="text-xs text-gray-400">.docx, .xlsx ou .xls — 25 Mo maximum</p>
              </label>
            </div>
            {validation && (
              <div className="text-xs">
                <p className="font-medium text-gray-700">{validation.fileName} — {validation.sizeLabel}</p>
                {validation.errors.map((message) => <p key={message} className="text-red-600">✕ {message}</p>)}
                {validation.warnings.map((message) => <p key={message} className="text-amber-600">⚠ {message}</p>)}
              </div>
            )}
          </Step>

          <Step index={2} title="Reconnaissance du contenu" status={step2}>
            {parsing && <p>Lecture du document…</p>}
            {parseError && <p className="text-red-600">Lecture impossible : {parseError}</p>}
            {preview && (
              <>
                <div className="grid grid-cols-3 gap-2">
                  <Metric value={preview.positions.length} label="Postes" />
                  <Metric value={preview.totalGroups} label="Groupes" />
                  <Metric value={preview.totalSkills} label="Compétences" />
                  {preview.positionsWithoutSkills > 0 && (
                    <p className="text-xs text-amber-700 mt-2 col-span-3">
                      {preview.positionsWithoutSkills} poste(s) sans compétences (sommaire) — ils seront importés quand même.
                    </p>
                  )}
                </div>
                {preview.totalSkills === 0 && (
                  <p className="text-amber-600 text-xs">
                    Aucune compétence reconnue. Vérifiez que chaque groupe s’écrit « Nom du groupe : compétence. compétence. ».
                  </p>
                )}
              </>
            )}
          </Step>

          <Step index={3} title="Cohérence avec l'existant" status={step3}>
            <p className="text-xs text-gray-500">
              Le fichier fait référence pour le secteur choisi : un poste reconnu à son intitulé voit ses compétences
              remplacées, un poste inconnu est créé. Un poste du même intitulé dans un autre secteur reste un poste
              distinct et n’est jamais modifié.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Secteur cible</label>
                <select
                  value={sectorId}
                  onChange={(e) => { setSectorId(e.target.value); setSubsectorId(''); resetDownstream(); }}
                  disabled={busy}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00244F]/20"
                >
                  <option value="">Choisir un secteur</option>
                  {openSectors.map((sector) => <option key={sector.id} value={sector.id}>{sector.nameFr}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Sous-secteur (optionnel)</label>
                <select
                  value={subsectorId}
                  onChange={(e) => { setSubsectorId(e.target.value); resetDownstream(); }}
                  disabled={busy}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00244F]/20"
                >
                  <option value="">Aucun</option>
                  {openSubsectors.map((sub) => (
                    <option key={sub.id} value={sub.id}>{sub.category ? `${sub.category} – ${sub.nameFr}` : sub.nameFr}</option>
                  ))}
                </select>
              </div>
            </div>

            <button
              onClick={handleAnalyze}
              disabled={!preview || !sectorId || analyzing || busy}
              className="w-full px-4 py-2 text-sm font-medium text-[#00244F] border border-[#00244F]/30 hover:bg-[#00244F]/5 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {analyzing ? 'Analyse en cours…' : 'Analyser la cohérence'}
            </button>

            {analyzeError && <p className="text-red-600 text-xs">Analyse impossible : {analyzeError}</p>}

            {plan && (
              <>
                <div className="grid grid-cols-4 gap-2">
                  <Metric value={plan.counts.positionsNew + plan.counts.groupsNew + plan.counts.skillsNew} label="À créer" tone="emerald" />
                  <Metric value={plan.counts.positionsUpdated + plan.counts.groupsUpdated + plan.counts.skillsUpdated} label="À mettre à jour" tone="blue" />
                  <Metric value={replacedCount} label="À retirer (remplacés)" tone="amber" />
                  <Metric value={plan.counts.discarded} label="Lignes écartées" tone="red" />
                </div>
                <p className="text-xs text-gray-500">
                  {plan.counts.positionsUpdated} poste(s) du secteur sont repris par le fichier : leurs compétences sont
                  remplacées par celles du fichier. {plan.counts.positionsNew} poste(s) sont créés.
                  {absentCount > 0
                    ? ` ${absentCount} poste(s) du secteur (${absentChildrenCount} éléments) ne sont pas dans le fichier : à décider à l’étape 4.`
                    : ' Le fichier couvre tous les postes actifs du secteur.'}
                </p>
                {plan.issues.length > 0 && (
                  <div className="max-h-44 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                    {plan.issues.slice(0, 200).map((issue, index) => (
                      <div key={index} className="px-3 py-2 text-xs">
                        <span className={issue.level === 'blocking' ? 'text-red-600 font-medium' : 'text-amber-600 font-medium'}>
                          {issue.level === 'blocking' ? '✕' : '⚠'} {issue.target}
                        </span>
                        <span className="text-gray-600"> — {issue.detail}</span>
                      </div>
                    ))}
                    {plan.issues.length > 200 && (
                      <div className="px-3 py-2 text-xs text-gray-400">… et {plan.issues.length - 200} autres</div>
                    )}
                  </div>
                )}
              </>
            )}
          </Step>

          <Step index={4} title="Préparation et lancement en arrière-plan" status={step4}>
            <label className="flex items-center gap-2 text-xs">
              <input type="checkbox" checked={reactivateObsolete} disabled={busy} onChange={(e) => { setReactivateObsolete(e.target.checked); resetDownstream(); }} />
              Réactiver les éléments devenus obsolètes que le fichier contient encore
            </label>
            {absentCount > 0 && (
              <label className="flex items-start gap-2 text-xs bg-amber-50 border border-amber-200 rounded-lg p-2">
                <input
                  type="checkbox"
                  checked={obsoleteAbsent}
                  disabled={busy}
                  onChange={(e) => setObsoleteAbsent(e.target.checked)}
                  className="mt-0.5"
                />
                <span>
                  Le fichier décrit le secteur en entier : rendre obsolètes les {absentCount} poste(s) qu’il ne
                  mentionne pas, et leurs {absentChildrenCount} élément(s).
                  {(plan?.impact.absentPositionsOffers ?? 0) + (plan?.impact.absentPositionsExperiences ?? 0) > 0 && (
                    <span className="block text-amber-700">
                      {plan?.impact.absentPositionsOffers} offre(s) et {plan?.impact.absentPositionsExperiences} expérience(s)
                      candidat les référencent. Rien n’est supprimé : elles restent lisibles.
                    </span>
                  )}
                </span>
              </label>
            )}
            <label className="flex items-center gap-2 text-xs">
              <input type="checkbox" checked={translate} disabled={busy} onChange={(e) => setTranslate(e.target.checked)} />
              Traduire les nouveaux libellés dans les {TRANSLATION_LANGS.length} langues après création
            </label>
            <p className="text-xs text-gray-500">
              Le traitement s’exécute en arrière-plan : vous pouvez refermer cette fenêtre et continuer à travailler.
              Évitez en revanche de recharger la page tant qu’il tourne.
            </p>
            <div className="flex gap-2">
              <button
                onClick={handleStart}
                disabled={!plan || busy}
                className="flex-1 px-4 py-3 text-sm font-medium text-white bg-[#00244F] hover:bg-[#001B3D] rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {busy ? 'Import en cours…' : 'Lancer l’import'}
              </button>
              {busy && (
                <button onClick={cancelImport} className="px-4 py-3 text-sm font-medium text-red-600 border border-red-200 hover:bg-red-50 rounded-lg">
                  Interrompre
                </button>
              )}
            </div>
          </Step>

          <Step index={5} title="Création des données" status={step5}>
            {runner.progress && (
              <>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-[#00244F] transition-all duration-300" style={{ width: `${progressPct}%` }} />
                </div>
                <p className="text-xs text-gray-600">{runner.progress.label} — {progressPct} %</p>
              </>
            )}
            {runner.status === 'failed' && <p className="text-red-600 text-xs">Échec : {runner.error}</p>}
            {runner.status === 'cancelled' && <p className="text-amber-600 text-xs">Interrompu à votre demande. Les lignes déjà écrites sont conservées.</p>}
            {!runner.progress && runner.status === 'idle' && <p className="text-xs text-gray-500">En attente du lancement.</p>}
          </Step>

          <Step index={6} title="Rapport d'import" status={step6}>
            {!runner.report && <p className="text-xs text-gray-500">Disponible à la fin du traitement.</p>}
            {runner.report && (
              <>
                <div className="grid grid-cols-4 gap-2">
                  <Metric
                    value={runner.report.created.positions + runner.report.created.skill_groups + runner.report.created.skills}
                    label="Créés"
                    tone="emerald"
                  />
                  <Metric
                    value={runner.report.updated.positions + runner.report.updated.skill_groups + runner.report.updated.skills}
                    label="Mis à jour"
                  />
                  <Metric
                    value={runner.report.retired.positions + runner.report.retired.skill_groups + runner.report.retired.skills}
                    label="Rendus obsolètes"
                    tone="amber"
                  />
                  <Metric value={runner.report.failures.length} label="Blocages" tone={runner.report.failures.length ? 'red' : 'emerald'} />
                </div>
                <p className="text-xs text-gray-500">
                  Postes {runner.report.created.positions} créés / {runner.report.updated.positions} mis à jour /
                  {' '}{runner.report.retired.positions} retirés ·
                  groupes {runner.report.created.skill_groups} / {runner.report.updated.skill_groups} / {runner.report.retired.skill_groups} ·
                  compétences {runner.report.created.skills} / {runner.report.updated.skills} / {runner.report.retired.skills} ·
                  en {(runner.report.durationMs / 1000).toFixed(1)} s
                  {runner.report.discarded > 0 && ` · ${runner.report.discarded} lignes écartées`}
                  {runner.report.translationsQueued > 0 && ` · ${runner.report.translationsQueued} libellés envoyés en traduction`}
                  {runner.report.notAttempted > 0 && ` · ${runner.report.notAttempted} lignes jamais tentées après l’arrêt`}
                </p>
                {runner.report.abortedReason && (
                  <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg p-2">{runner.report.abortedReason}</p>
                )}
                {runner.report.failures.length > 0 && (
                  <div className="max-h-44 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                    {runner.report.failures.slice(0, 200).map((failure, index) => (
                      <div key={index} className="px-3 py-2 text-xs">
                        <span className="text-red-600 font-medium">{failure.name}</span>
                        <span className="text-gray-400"> ({failure.code})</span>
                        <span className="text-gray-600"> — {failure.cause}</span>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex gap-2">
                  <button onClick={downloadReport} className="px-4 py-2 text-xs font-medium text-[#00244F] border border-[#00244F]/30 hover:bg-[#00244F]/5 rounded-lg">
                    Télécharger le rapport
                  </button>
                  <button onClick={onClose} className="px-4 py-2 text-xs font-medium text-white bg-[#00244F] hover:bg-[#001B3D] rounded-lg">
                    Terminer
                  </button>
                </div>
                <p className="text-[11px] text-gray-400">
                  Le recalcul des compétences proches n’est plus lancé automatiquement : relancez-le depuis
                  Command board → Proximité des compétences quand vos imports sont terminés.
                </p>
              </>
            )}
          </Step>
        </div>
      </div>
    </div>
  );
};

export default ImportWizard;
